import { Channel } from "@/config/channel"
import { getConfigParams } from "@/lib"
import { parseDateString } from "@/lib/parse-date-string"
import useSignupRequestStore from "@/store/signup-request-store"
import { Err, isOk, Ok, Result } from "@pacote/result"
import { useQuery } from "@tanstack/react-query"
import { z } from "zod"
import { BaseApiErrorResponse } from "./api-config"
import { baseExternalFetcher } from "./base-fetchers"
import { useRemoteConfigParamsSignupFeQuery } from "./get-remote-config-firebase"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/
type BonusPayload = {
  isOnline: boolean
  isPdv: boolean
  siteId: Channel
}

const bonusSchema = z.object({
  sgadId: z.number(),
  cardId: z.number(),

  // this must always be present in order to generate a visible bonus card,
  // but may not come from the service. Adjust the validations accordingly
  bonusCode: z.string(),

  datePublication: z.string(),
  dateExpiration: z.string(),
  description: z.string(),
  style: z.string().optional(),
  title: z.string(),
  urlReadMore: z.string().optional(),
  urlImage: z.string(),
  detailLabel: z.string().optional(),
  ctaPrimaryLabel: z.string().optional(),
  ctaPrimaryLink: z.string().optional(),
  canali: z.array(
    z.object({
      idSite: z.number().optional(),
      isOnline: z.boolean().optional(),
      isPdv: z.boolean().optional(),
      isActive: z.boolean().optional(),
      orderNumber: z.number().optional(),
    }),
  ),
  urlImageDesk: z.string().optional(),
  urlImageMobile: z.string().optional(),
  urlImageHpDesk: z.string().optional(),
  urlImageHpMobile: z.string().optional(),
  prodotto: z.string().optional(),
  slug: z.string().optional(),
})

type Bonus = z.infer<typeof bonusSchema>

// la parte del bonus che salveremo nello store
type PartialBonus = Pick<
  Bonus,
  | "title"
  | "description"
  | "style"
  | "urlReadMore"
  | "detailLabel"
  | "bonusCode"
> & {
  isPromoCode?: boolean
  slug?: string
}

type BonusResponse = {
  bonus?: z.infer<typeof bonusSchema>[]
}

type TBonusResponse = {
  bonus: BonusResponse["bonus"]
  activeBonus: Bonus[]
}

type BonusErrorResponse = BaseApiErrorResponse

type TbonusErrorResponse = {
  type: "ERROR-MESSAGE"
  errorMessage: string
}

type BonusServiceResponse = Result<TBonusResponse, TbonusErrorResponse>

/* -------------------------------------------------------------------------------------------------
 * helpers
 * -----------------------------------------------------------------------------------------------*/
function filterByBonusDateValidity(bonus: Bonus) {
  const today = new Date()

  const [datePublication] = parseDateString(bonus.datePublication.split(" ")[0])
  const [dateExpiration] = parseDateString(bonus.dateExpiration.split(" ")[0])

  // Get the difference in days between today and publication/expiration dates
  const diffPubInMs = today.getTime() - datePublication.getTime()
  const diffExpInMs = today.getTime() - dateExpiration.getTime()

  // Convert difference in milliseconds to days (round down to whole days)
  const diffPubDays = Math.floor(diffPubInMs / (1000 * 60 * 60 * 24))
  const diffExpDays = Math.floor(diffExpInMs / (1000 * 60 * 60 * 24))

  return diffPubDays >= 0 && diffExpDays <= 0
}

function isActive(bonus: Bonus, channel: Channel) {
  return (
    bonus.canali.find((canale) => canale.idSite === channel)?.isActive ?? false
  )
}

function getActiveBonusList(bonusList: Bonus[], channel: Channel) {
  return bonusList
    .filter(
      (item) =>
        item.bonusCode != null &&
        bonusSchema.safeParse(item).success &&
        isActive(item, channel),
    )
    .filter(filterByBonusDateValidity)
}
/* -------------------------------------------------------------------------------------------------
 * fetcher
 * -----------------------------------------------------------------------------------------------*/

const bonusFetcher = async (
  params: BonusPayload,
): Promise<BonusServiceResponse> => {
  const bonusServiceEndpoint = window.BONUS_LIST_ENDPOINT

  const responseResult = await baseExternalFetcher<
    BonusResponse,
    BonusErrorResponse
  >({
    url: bonusServiceEndpoint,
    params,
    errorSchemaValidator: null,
    successSchemaValidator: null,
  })

  if (isOk(responseResult)) {
    const bonus = responseResult.value.bonus

    // service can have 200 status but totally empty reponse, this redirect to the promo error page
    if (bonus == null) {
      return Ok({ bonus: [], activeBonus: [] })
    }

    const { channel } = getConfigParams()
    const activeBonus = getActiveBonusList(bonus, channel)

    return Ok({ bonus, activeBonus })
  }

  const { data } = responseResult.value

  const errorInfos = data.error?.errorInfos

  if (errorInfos != null) {
    const nameError = Object.keys(errorInfos)[0]
    const errorMessage = errorInfos[nameError][0]

    return Err({ type: "ERROR-MESSAGE", errorMessage })
  } else {
    return Err({ type: "ERROR-MESSAGE", errorMessage: "Unhandled API error" })
  }
}

/* -------------------------------------------------------------------------------------------------
 * hooks
 * -----------------------------------------------------------------------------------------------*/

const useBonusListQuery = () => {
  const { data } = useSignupRequestStore()
  const { channel } = getConfigParams()

  const remoteConfigParamsQuery = useRemoteConfigParamsSignupFeQuery()

  const isPdv = data.fromPdv
  const isOnline = !data.fromPdv
  const siteId = channel

  return useQuery({
    queryKey: ["bonus-query", isPdv, isOnline, siteId],
    queryFn: () => bonusFetcher({ isPdv, isOnline, siteId }),
    meta: { preventGlobalError: true },
    staleTime: Infinity,
    enabled:
      remoteConfigParamsQuery.isFetched &&
      remoteConfigParamsQuery.data?.welcome_bonus_enabled !== false,
  })
}

export { useBonusListQuery }
export type { Bonus, BonusServiceResponse, PartialBonus }
